import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Fingerprint,
  FolderTree,
  Gauge,
  Globe2,
  KeyRound,
  LockKeyhole,
  MonitorSmartphone,
  Send,
  ServerOff,
  ShieldCheck,
  Sparkles,
  Users,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FlowVisual } from '@/components/connection/flow-visual';
import { RoomCodeInput } from '@/components/connection/room-code-input';
import { PendingTray } from '@/components/transfer/pending-tray';
import { DropZone } from '@/components/transfer/drop-zone';
import { FileTypeIcon } from '@/components/transfer/file-icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn } from '@/lib/utils';
import { roomController } from '@/features/rooms/room-controller';
import { useHistoryStore } from '@/stores/history-store';
import { usePendingFilesStore } from '@/stores/pending-files-store';
import { useSessionStore } from '@/stores/session-store';
import {
  formatBytes,
  formatRelativeTime,
  isRoomCode,
} from '@flowshare/shared';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/80">
      {children}
    </span>
  );
}

export function HomePage() {
  useDocumentTitle('FlowShare — Share files. Directly. Anywhere.');
  const navigate = useNavigate();

  const phase = useSessionStore((state) => state.phase);
  const room = useSessionStore((state) => state.room);
  const peers = useSessionStore((state) => state.peers);
  const addFiles = usePendingFilesStore((state) => state.add);
  const pendingFiles = usePendingFilesStore((state) => state.files);
  const history = useHistoryStore((state) => state.entries);

  const [code, setCode] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const inRoom = phase === 'connected' || phase === 'reconnecting';
  const connectedPeers = Object.values(peers).filter(
    (peer) => !peer.isSelf && peer.connectionState === 'connected',
  );

  const handleCreateRoom = async () => {
    setCreating(true);
    const created = await roomController.createRoom();
    setCreating(false);
    if (created) navigate(`/r/${created}`);
  };

  const handleJoin = (value: string) => {
    if (!isRoomCode(value)) return;
    navigate(`/r/${value}`);
  };

  const handleFiles = (files: File[]) => {
    addFiles(files);
  };

  const handleSendPending = () => {
    if (inRoom && room) {
      if (connectedPeers.length > 0) {
        const count = roomController.sendFiles(
          pendingFiles,
          connectedPeers.map((peer) => peer.id),
        );
        if (count > 0) usePendingFilesStore.getState().clear();
        navigate(`/transfers`);
      } else {
        // Stay in the current room and wait for a device instead of creating
        // another room and silently abandoning this one.
        navigate(`/r/${room.code}`);
      }
      return;
    }
    void handleCreateRoom();
  };

  const recent = history.slice(0, 3);

  return (
    <div className="space-y-16 sm:space-y-20">
      {/* ------------------------------------------------------------------ */}
      {/* Hero                                                               */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative pt-2 sm:pt-6">
        <div className="grid items-center gap-12 lg:grid-cols-[1.06fr_0.94fr] lg:gap-10">
          <div className="animate-fade-up">
            <Badge variant="outline" className="gap-2 border-border/80 bg-card/40 py-1 pl-2 pr-3">
              <span className="status-dot bg-primary" />
              Peer-to-peer · No uploads
            </Badge>

            <h1 className="mt-5 text-[42px] font-semibold leading-[0.98] tracking-[-0.055em] sm:text-[58px] lg:text-[64px]">
              Share files.
              <br />
              <span className="gradient-text">Directly. Anywhere.</span>
            </h1>

            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Move files between any two browsers — across the room or across the world.
              FlowShare opens a private, encrypted line between devices. Your files never
              touch a server.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2.5 text-[12px] text-muted-foreground">
              {[
                { icon: ServerOff, label: 'No cloud copies' },
                { icon: KeyRound, label: 'No account' },
                { icon: MonitorSmartphone, label: 'Any OS · any browser' },
              ].map((item) => (
                <span key={item.label} className="inline-flex items-center gap-1.5">
                  <item.icon className="size-3.5 text-primary/80" />
                  {item.label}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => void handleCreateRoom()} disabled={creating}>
                {creating ? (
                  <span className="size-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                ) : (
                  <Sparkles />
                )}
                {creating ? 'Opening room…' : 'Create a room'}
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#how-it-works">
                  How it works
                  <ArrowRight />
                </a>
              </Button>
            </div>

            <Link
              to="/offline"
              className="mt-4 inline-flex items-center gap-2 text-[12px] font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              <WifiOff className="size-3.5" />
              No internet? Pair two devices directly with QR codes
              <ArrowRight className="size-3.5" />
            </Link>
          </div>

          <div
            className="animate-fade-up overflow-hidden lg:overflow-visible lg:justify-self-end"
            style={{ animationDelay: '120ms' }}
          >
            <FlowVisual />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Pending files                                                      */}
      {/* ------------------------------------------------------------------ */}
      {pendingFiles.length > 0 ? (
        <PendingTray
          className="animate-fade-up"
          title={inRoom ? 'Ready to send' : 'Files selected'}
          hint={
            inRoom
              ? 'Choose a room device on the next screen, or send to everyone connected now.'
              : 'Create a room, then share the code with the other device.'
          }
          actionLabel={
            inRoom
              ? connectedPeers.length > 0
                ? `Send to ${connectedPeers.length} device${connectedPeers.length === 1 ? '' : 's'}`
                : 'Open room'
              : 'Create a room'
          }
          actionIcon={inRoom && connectedPeers.length > 0 ? 'send' : 'arrow'}
          onAction={handleSendPending}
          actionDisabled={creating}
        />
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Action deck                                                        */}
      {/* ------------------------------------------------------------------ */}
      <section className="grid gap-5 lg:grid-cols-[1.12fr_0.88fr]">
        <div className="animate-fade-up min-w-0" style={{ animationDelay: '80ms' }}>
          <DropZone onFiles={handleFiles} />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {inRoom && room ? (
            <Card className="animate-fade-up overflow-hidden p-5" style={{ animationDelay: '120ms' }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <SectionLabel>
                    <span className="status-dot bg-primary" />
                    Room active
                  </SectionLabel>
                  <p className="mt-2 font-mono text-3xl font-semibold tracking-[0.12em]">
                    {room.code}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {Object.keys(peers).length} device{Object.keys(peers).length === 1 ? '' : 's'} in
                    the room
                  </p>
                </div>
                <Badge variant="success">
                  <Wifi className="size-3" />
                  Live
                </Badge>
              </div>
              <Button className="mt-5 w-full" asChild>
                <Link to={`/r/${room.code}`}>
                  Open room
                  <ArrowRight />
                </Link>
              </Button>
            </Card>
          ) : (
            <Card className="animate-fade-up overflow-hidden" style={{ animationDelay: '120ms' }}>
              <div className="p-5 pb-4">
                <SectionLabel>
                  <Zap className="size-3 text-primary" />
                  Start a room
                </SectionLabel>
                <h2 className="mt-2.5 text-xl font-semibold tracking-[-0.035em]">
                  Open a direct line
                </h2>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                  Get a six-character code and a link. Share it with any device to start
                  transferring.
                </p>

                <Button
                  className="mt-4 w-full"
                  variant="brand"
                  size="lg"
                  onClick={() => void handleCreateRoom()}
                  disabled={creating}
                >
                  {creating ? (
                    <span className="size-4 animate-spin rounded-full border-2 border-black/20 border-t-black" />
                  ) : (
                    <Send />
                  )}
                  {creating ? 'Opening room…' : 'Create room'}
                </Button>
              </div>

              <div className="relative px-5">
                <div className="hairline" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60">
                  or join
                </span>
              </div>

              <div className="p-5 pt-4">
                <div className="flex flex-col items-center gap-3 sm:flex-row">
                  <RoomCodeInput
                    value={code}
                    onChange={setCode}
                    onComplete={handleJoin}
                    className="w-full max-w-[280px] shrink-0"
                  />
                  <Button
                    className="w-full sm:w-auto"
                    variant="secondary"
                    onClick={() => handleJoin(code)}
                    disabled={code.length !== 6}
                  >
                    Join room
                    <ArrowRight />
                  </Button>
                </div>
                <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground/70">
                  Rooms expire after 2 hours and hold up to 8 devices. No sign-up, no
                  file storage.
                </p>
              </div>
            </Card>
          )}

          <div
            className="animate-fade-up grid grid-cols-3 gap-2"
            style={{ animationDelay: '180ms' }}
          >
            {[
              { icon: Gauge, value: '64 KB', label: 'chunks' },
              { icon: ShieldCheck, value: 'SHA-256', label: 'verified' },
              { icon: Globe2, value: 'Global', label: 'direct' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-border/60 bg-card/40 px-2.5 py-3 text-center"
              >
                <stat.icon className="mx-auto size-3.5 text-primary/80" />
                <p className="mt-1.5 text-[12px] font-semibold leading-none">{stat.value}</p>
                <p className="mt-1 text-[9px] uppercase tracking-wider text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Recent activity                                                    */}
      {/* ------------------------------------------------------------------ */}
      {recent.length > 0 ? (
        <section className="animate-fade-up">
          <div className="mb-3 flex items-center justify-between">
            <SectionLabel>
              <Fingerprint className="size-3" />
              Recent transfers
            </SectionLabel>
            <Link
              to="/history"
              className="text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              View all
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {recent.map((entry) => (
              <Link
                key={entry.id}
                to="/history"
                className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/35 px-3 py-2.5 transition-colors hover:border-border hover:bg-card/60"
              >
                <FileTypeIcon name={entry.fileName} size="sm" />
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-medium">{entry.fileName}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatBytes(entry.fileSize)} · {formatRelativeTime(entry.completedAt)}
                  </p>
                </div>
                {entry.status === 'completed' ? (
                  <Check className="ml-auto size-3.5 shrink-0 text-primary" />
                ) : (
                  <ArrowUpRight className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
                )}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* How it works                                                       */}
      {/* ------------------------------------------------------------------ */}
      <section id="how-it-works" className="scroll-mt-24">
        <div className="max-w-2xl">
          <SectionLabel>
            <Sparkles className="size-3" />
            Three steps
          </SectionLabel>
          <h2 className="mt-3 text-[30px] font-semibold leading-[1.05] tracking-[-0.045em] sm:text-[38px]">
            From your device to theirs.
            <br />
            <span className="text-muted-foreground">No detours.</span>
          </h2>
        </div>

        <div className="relative mt-8 grid gap-4 md:grid-cols-3">
          <div className="absolute left-[16%] right-[16%] top-[46px] hidden border-t border-dashed border-border/70 md:block" />
          {[
            {
              step: '01',
              icon: Users,
              title: 'Open a room',
              copy: 'Create a room in one click. You get a short code, a link and a QR — no account required.',
            },
            {
              step: '02',
              icon: MonitorSmartphone,
              title: 'Link the device',
              copy: 'Open FlowShare on the other device and join. WebRTC negotiates the fastest encrypted path.',
            },
            {
              step: '03',
              icon: FolderTree,
              title: 'Send the files',
              copy: 'Drop files or entire folders. Chunks stream with backpressure and SHA-256 verification.',
            },
          ].map((item) => (
            <Card key={item.step} className="relative p-5 transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex items-center justify-between">
                <span className="grid size-9 place-items-center rounded-xl border border-primary/20 bg-primary/[0.08] text-primary">
                  <item.icon className="size-4" strokeWidth={1.8} />
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/50">
                  {item.step}
                </span>
              </div>
              <h3 className="mt-4 text-[15px] font-semibold tracking-[-0.02em]">{item.title}</h3>
              <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">{item.copy}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Privacy                                                            */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative overflow-hidden rounded-3xl border border-border/70 bg-card/45 p-6 sm:p-9">
        <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-primary/[0.07] blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <div>
            <SectionLabel>
              <LockKeyhole className="size-3" />
              Privacy model
            </SectionLabel>
            <h2 className="mt-3 text-[28px] font-semibold leading-[1.08] tracking-[-0.045em] sm:text-[36px]">
              The server never
              <br />
              sees your files.
            </h2>
            <p className="mt-3 max-w-md text-[13px] leading-relaxed text-muted-foreground">
              FlowShare's backend only helps two browsers find each other. Once the
              connection is open, every byte moves through a DTLS-encrypted WebRTC data
              channel — straight from one device to the other.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {['No file storage', 'No accounts', 'No tracking pixels', 'No file scans'].map(
                (item) => (
                  <Badge key={item} variant="secondary" className="gap-1.5 py-1">
                    <Check className="size-3 text-primary" />
                    {item}
                  </Badge>
                ),
              )}
            </div>
          </div>

          <div className="space-y-2">
            {[
              {
                icon: ServerOff,
                title: 'Signaling server',
                copy: 'Exchanges SDP and ICE candidates. Never receives file data.',
                tone: 'text-muted-foreground',
              },
              {
                icon: LockKeyhole,
                title: 'Encrypted data channel',
                copy: 'DTLS-SRTP encryption with ephemeral keys. SHA-256 verifies every file.',
                tone: 'text-primary',
              },
              {
                icon: MonitorSmartphone,
                title: 'Your devices',
                copy: 'Files are read from disk in 64 KB chunks and written straight to the receiver.',
                tone: 'text-brand-2',
              },
            ].map((row, index) => (
              <div
                key={row.title}
                className="relative flex items-start gap-3 rounded-xl border border-border/60 bg-background/40 p-3.5"
              >
                <span className={cn('mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-border/60 bg-card', row.tone)}>
                  <row.icon className="size-4" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-[12px] font-medium">{row.title}</p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{row.copy}</p>
                </div>
                {index < 2 ? (
                  <ArrowUpRight className="absolute -bottom-3 left-7 size-3 rotate-45 text-border" />
                ) : null}
              </div>
            ))}
            <p className="px-1 pt-1 text-[10px] leading-relaxed text-muted-foreground/60">
              On restrictive networks an optional TURN relay forwards encrypted packets. It
              cannot read them.
            </p>
          </div>
        </div>
      </section>

      <footer className="flex flex-col items-center justify-between gap-4 border-t border-border/50 pt-6 text-[11px] text-muted-foreground/70 sm:flex-row">
        <p>FlowShare · Share files. Directly. Anywhere.</p>
        <div className="flex items-center gap-4">
          <Link to="/about" className="transition-colors hover:text-foreground">
            About
          </Link>
          <Link to="/settings" className="transition-colors hover:text-foreground">
            Settings
          </Link>
          <span className="font-mono text-[10px] text-muted-foreground/40">v1.0.0</span>
        </div>
      </footer>
    </div>
  );
}
